import { PARTIAL_SPEAKER_LABELS } from '@renderer/hooks/audio/use-live-transcript-preview'
import type { ShownPartial } from '@renderer/hooks/audio/use-held-partial'
import { formatTranscriptTime } from '@renderer/utils/audio-note-helpers'
import { trimRepeatedWords } from '@renderer/hooks/audio/text-overlap'
import type { Message } from './types'

/** A turn opened by in-flight speech alone: no final has landed for it yet. */
export function isPendingTurn(message: Message): boolean {
  return message.segments.length === 0 && message.pendingText !== undefined
}

/**
 * Puts the in-flight line where its final will land, so the final swaps text
 * in place: at the tail of the last turn when it's from the same source (turns
 * are whole speaker runs, see `mergeSpeakerTurns`), or else into a pending turn
 * of its own. Partials carry no diarized speaker, so a pending turn is headed
 * "You" or "Them", and a system-audio partial can trail the previous remote
 * speaker until its final names who said it.
 */
export function placeLivePartial(turns: Array<Message>, partial: ShownPartial | undefined): Array<Message> {
  if (!partial) return turns
  const isHost = partial.source === 'microphone'
  const last = turns[turns.length - 1]
  if (last?.isHost === isHost) {
    // The live line can start by repeating the end of the final it follows
    const text = trimRepeatedWords(last.text, partial.text)
    return text ? [...turns.slice(0, -1), { ...last, pendingText: text }] : turns
  }
  const startDate = new Date(partial.startedAt)
  const pendingTurn: Message = {
    sender: PARTIAL_SPEAKER_LABELS[partial.source],
    text: '',
    segments: [],
    time: startDate.toISOString(),
    displayTime: formatTranscriptTime(startDate),
    isHost,
    pendingText: partial.text,
  }
  return [...turns, pendingTurn]
}
